import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { customFieldService } from '../../services/custom-field.service'
import { PermissionAction, PermissionType } from '../../services/permissions.service'
import { CustomField } from '../../types'
import IfPermissions from '../common/IfPermissions'
import { ManagementList } from '../common/ManagementList'
import { CustomFieldEditDialog } from './CustomFieldEditDialog'
import { CloneCustomFieldsToOrganizationsModal } from './CloneCustomFieldsToOrganizationsModal'

export const CustomFields: React.FC = () => {
  const { t } = useTranslation()
  const [showCloneModal, setShowCloneModal] = useState(false)
  const [cloneModalInitialFieldIds, setCloneModalInitialFieldIds] = useState<number[]>([])

  const openCloneModal = (selectedIds: Set<number>) => {
    setCloneModalInitialFieldIds(Array.from(selectedIds))
    setShowCloneModal(true)
  }

  const closeCloneModal = () => {
    setShowCloneModal(false)
    setCloneModalInitialFieldIds([])
  }

  const getDeleteMessage = (object: CustomField) => {
    return t('confirm_delete_field_message', { name: object.name })
  }

  const extraColumns = [
    {
      key: 'data_type',
      name: t('type'),
      valueFn: (field: CustomField) => {
        const typeLabels: { [key: string]: string } = {
          string: t('text'),
          url: t('url'),
          date: t('date'),
          boolean: t('boolean'),
          integer: t('number'),
          separator: t('separator'),
          float: t('decimal'),
          monetary: t('monetary'),
          documentlink: t('document_link'),
          longtext: t('long_text'),
          select: t('selection'),
          counter: t('counter_data_type'),
        }
        return <span className="badge bg-secondary">{typeLabels[field.data_type] || field.data_type}</span>
      },
    },
  ]

  const headerActions = (selectedIds: Set<number>) => (
    <IfPermissions action={PermissionAction.Add} type={PermissionType.CustomField}>
      <button
        type="button"
        className="btn btn-sm btn-outline-primary me-2"
        onClick={() => openCloneModal(selectedIds)}
      >
        <i className="bi bi-files me-1"></i>
        {t('clone_to_organizations') || 'Clone to organizations'}
      </button>
    </IfPermissions>
  )

  return (
    <>
      <ManagementList
        service={customFieldService}
        editDialogComponent={CustomFieldEditDialog}
        typeName={t('custom_field')}
        typeNamePlural={t('custom_fields')}
        permissionType="custom_field"
        extraColumns={extraColumns}
        getDeleteMessage={getDeleteMessage}
        info={t('custom_fields_info')}
        infoLink="custom_fields"
        hideMatchingColumn
        headerActions={headerActions}
        documentCountValueFn={(field: CustomField) => (
          field.data_type === 'separator' ? <span className="text-muted">—</span> : field.document_count
        )}
        isDocumentCountDisabled={() => true}
      />
      <CloneCustomFieldsToOrganizationsModal
        show={showCloneModal}
        onClose={closeCloneModal}
        initialSelectedFieldIds={cloneModalInitialFieldIds}
      />
    </>
  )
}
